import { useEffect, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { Navigate, Route, Routes } from "react-router-dom";
import { AuthenticationGuard } from "./auth0/AuthenticationGuard/AuthenticationGuard";
import {Box, Alert, Button, CircularProgress} from '@mui/material';
import Glossary from "./pages/basic-pages/Glossary/Glossary";
import PageNotFound from "./pages/basic-pages/PageNotFound/PageNotFound";
import CustomGroupEditor from './pages/boards/CustomGroupEditor/CustomGroupEditor';
import DataViewer from './pages/boards/DataViewer/DataViewer';
import HarvestCycle from './pages/boards/HarvestCycle/HarvestCycle';
import DistrictExplorer from './pages/boards/DistrictExplorer/DistrictExplorer';
import DistrictInsight from './pages/boards/DistrictInsight/DistrictInsight';
import ElectionHistory from './pages/boards/ElectionHistory';
import HealthBenefits from './pages/boards/HealthBenefits/HealthBenefits.js'
import LiveBirthDashboard from './pages/boards/LiveBirthDashboard/LiveBirthDashboard';
import SalaryDashboard from './pages/boards/SalaryDashboard/SalaryDashboard';
import SalarySchedules from './pages/boards/SalarySchedules/SalarySchedules';
import Groups from './pages/cruds/Groups';
import Users from "./pages/cruds/Users";
import AccessDenied from "./pages/basic-pages/AccessDenied/AccessDenied";
import { setToken } from './utils/helpsers';
import CustomBoards from './pages/cruds/CustomBoards';
import CrudCustomGroups from './pages/cruds/CustomGroups';
import Profile from "./pages/basic-pages/Profile/Profile";
import axios from "axios";
import { TokenContext } from "./utils/Context";
import PrivacyPolicy from "./pages/basic-pages/PrivacyPolicy/PrivacyPolicy";
// Custom MUI theme configurations.
import customTheme from "./utils/customTheme";
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
// Swagger
import SwaggerPage from "./swagger/SwaggerPage";

function App() {
  const [userToken, setUserToken] = useState(window.localStorage.getItem('token'));
  const { isLoading, logout, getIdTokenClaims, user } = useAuth0();
  const [isNewVersionAvailable, setIsNewVersionAvailable] = useState(false);

  // Set or clear axios Authorization header when token changes
  // Important: Must clear header when token is null to prevent stale tokens
  // from previous sessions causing 401 errors on first login
  useEffect(() => {
    if (userToken) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${userToken}`;
    } else {
      delete axios.defaults.headers.common['Authorization'];
    }
  }, [userToken]);

  useEffect(() => {
    if (user) {
      var _paq = window._paq = window._paq || [];
      /* tracker methods like "setCustomDimension" should be called before "trackPageView" */
      _paq.push(['trackPageView']);
      _paq.push(['enableLinkTracking']);
      (function () {
        var u = `https://${process.env.REACT_APP_MATOMO_URL}/`;
        _paq.push(['setTrackerUrl', u + 'matomo.php']);
        _paq.push(['setSiteId', '2']);
        _paq.push(['setUserId', user.email + " - " + window.localStorage.getItem('session-type') + " - " + user[`${process.env.REACT_APP_AUTH0_KEY}/role`]]);
        var d = document, g = d.createElement('script'), s = d.getElementsByTagName('script')[0];
        g.async = true; g.src = `//cdn.matomo.cloud/${process.env.REACT_APP_MATOMO_URL}/matomo.js`; s.parentNode.insertBefore(g, s);
      })();
    }
  }, [user])

  // Option 2: Remove arbitrary setTimeout - update token immediately when available
  if (!window.localStorage.getItem('token')) {
    getIdTokenClaims().then(claims => {
      if (claims) {
        setToken(claims.__raw, claims.exp);
        window.localStorage.setItem('user_email', user.email);
        // Update token state immediately instead of after 1 second delay
        setUserToken(window.localStorage.getItem('token'));
      }
    }).catch(err => {
      console.debug("Something went wrong", err);
    });
  } else if (window.localStorage.getItem('exp')) {
    // We multiplying the expiration date by 1000 since it comes in a UNIX format
    // And it's necessary to convert it to a JavaScript format.
    var timeStampNow = new Date();
    var expDate = new Date(window.localStorage.getItem('exp') * 1000)

    if (expDate < timeStampNow) {
      localStorage.removeItem('fixedSelection');
      localStorage.removeItem('token');
      localStorage.removeItem('exp');
      localStorage.removeItem('session-type');
      localStorage.removeItem('user_email');

      // Clear token state immediately to remove stale Authorization header
      // before child components make API calls (Issue #415)
      setUserToken(null);

      logout({
        returnTo: window.location.origin,
      })
    }
  }

  useEffect(() => {    
    const storedVersionNumber = localStorage.getItem('appVersion');
    
    const currentVersionNumber = process.env.REACT_APP_VERSION_NUMBER
    
    localStorage.setItem('appVersion', currentVersionNumber);

    if (storedVersionNumber !== null && storedVersionNumber !== currentVersionNumber) {
      setIsNewVersionAvailable(true);
        }
  }, []);

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", marginTop: "10px" }}>
        <CircularProgress size={80} />
      </Box>
    );
  }

  return (
    <ThemeProvider theme={customTheme}>
      <CssBaseline />
      {isNewVersionAvailable &&
        <Alert
          severity="info"
          sx={{position: "fixed", bottom: "10px", left: "5px", zIndex: 1}}
          action={
            <Button color="inherit" size="small" onClick={() => {
              localStorage.clear();
              sessionStorage.clear()
              window.location.reload();
            }}>
              Refresh
            </Button>
          }
        >
          A new version of the app is available.
        </Alert>
      }
      <TokenContext.Provider value={{ userToken, setUserToken }}>
        <Routes>
          <Route path="/insights" element={<AuthenticationGuard component={DistrictInsight}/> }/>
          <Route path="/explorer" element={<AuthenticationGuard component={DistrictExplorer}/> }/>
          <Route path="/custom-groups" element={<AuthenticationGuard component={CustomGroupEditor}/> }/>
          <Route path="/election-history" element={<AuthenticationGuard component={ElectionHistory}/>} />
          <Route path="/health" element={<AuthenticationGuard component={HealthBenefits}/>} />
          <Route path="/salary" element={<AuthenticationGuard component={SalaryDashboard}/>} />
          <Route path="/schedules" element={<AuthenticationGuard component={SalarySchedules}/>} />
          <Route path="/live-birth" element={<AuthenticationGuard component={LiveBirthDashboard}/>} />
          <Route path="/glossary" element={<AuthenticationGuard component={Glossary}/>} />
          <Route path="/data-viewer" element={<AuthenticationGuard component={DataViewer}/>} />
          <Route path="/harvest" element={<AuthenticationGuard component={HarvestCycle}/>} />
          <Route path="/accounts" element={<AuthenticationGuard component={Groups}/>} />
          <Route path="/users" element={<AuthenticationGuard component={Users}/>} />
          <Route path="/custom-boards" element={<AuthenticationGuard component={CustomBoards}/>} />
          <Route path="/crud/custom-groups" element={<AuthenticationGuard component={CrudCustomGroups}/> }/>
          <Route path="/AccessDenied" element={<AccessDenied/>} />
          <Route path="/Profile" element={<AuthenticationGuard component={Profile}/>} />
          <Route path="/PrivacyPolicy" element={<AuthenticationGuard component={PrivacyPolicy}/>} />
          <Route path="/" element={<Navigate to="/insights" replace={true}/>} />
          <Route path="/api-doc" element={process.env.REACT_APP_API_URL === "https://test.insightboard.org/api"
              ?<AuthenticationGuard component={SwaggerPage}/>
              :<PageNotFound/>
          }/>
          <Route path="*" element={<AuthenticationGuard component={PageNotFound} />}/>
        </Routes>
      </TokenContext.Provider>
    </ThemeProvider>
  );
}

export default App;
